import type { ReactElement } from "react"
import { useMemo } from "react"

import { useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import { Grid, useMediaQuery, useTheme } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty as _isEmpty } from "lodash"

import type { FeedbackPerLabelAndReason } from "../../../types/generated/api/FeedbackPerLabelAndReason"
import { InsightList } from "../../../types/generated/api/InsightList"
import { RealTimeInsightsAPI } from "../Api"
import { LabelReasonGraph } from "../Components/LabelCriteriaInsight"
import { generateLabelCriteriaInsightData } from "../helpers"

/**
 * FeedbackPerLabelsAndReasons Component
 *
 * A container component that displays feedback data organized by labels and reasons.
 * It fetches and processes feedback data to show graphical representations of feedback
 * distribution across different labels and their associated reasons.
 *
 * Features:
 * - Responsive layout that shows either one or two graphs based on screen size
 * - Processes raw feedback data into a format suitable for visualization
 * - Handles loading and empty states
 *
 * @component
 */
export function FeedbackPerLabelsAndReasonsContainer(): ReactElement {
  // Extract project ID from URL and search parameters for date filtering
  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams, _] = useSearchParams()

  // Set up responsive layout breakpoints
  const theme = useTheme()
  const lg = useMediaQuery(theme.breakpoints.up("lg"))

  // Get date range from URL parameters
  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")

  // Fetch feedback data using react-query
  const {
    isLoading,
    data: feedbackCounts,
    isSuccess,
  } = useQuery<AxiosResponse<FeedbackPerLabelAndReason>, AxiosError>({
    queryKey: ["real_time_insights", "feedback_per_label_and_reason", projectId, startDate, endDate],
    queryFn: () =>
      RealTimeInsightsAPI.fetchFeedbackPerLabelAndReason({
        project_uuid: projectId as string,
        start_time: startDate as string,
        end_time: endDate as string,
      }),
    // Only fetch if all required parameters are present
    enabled: !!projectId && !!startDate && !!endDate,
  })

  // Check if we have any data to display
  const isEmpty = !feedbackCounts?.data.data || _isEmpty(feedbackCounts?.data.data)

  // Process the label data and calculate percentages
  const adjustedData = useMemo(
    () =>
      generateLabelCriteriaInsightData({
        data: feedbackCounts?.data,
        isLoading: isLoading,
        isEmpty: isEmpty,
      }),
    [feedbackCounts?.data, isEmpty, isLoading],
  )

  /**
   * Determines the number of graphs to display based on screen size.
   * Returns 2 for large screens if there's enough data, otherwise 1.
   *
   * @returns {number} Number of graphs to display (1 or 2)
   */
  const sliceCount = useMemo((): number => {
    const labelReasonsCount = Object.keys(adjustedData.labels).length

    // Show 2 graphs on large screens if there's enough data
    if (lg && labelReasonsCount) {
      return Math.min(2, labelReasonsCount)
    }

    return 1
  }, [adjustedData.labels, lg])

  return (
    <Grid container item xs={12}>
      {new Array(sliceCount).fill(0).map((_, index) => (
        <Grid
          item
          xs={sliceCount === 1 ? 12 : 6} // Full width for single graph, half width for two graphs
          pr={index === 0 && sliceCount !== 1 ? 2 : 0} // Add padding between graphs when showing two
          key={`label-criteria-${adjustedData.labels[index]}-${isLoading}`}
        >
          <LabelReasonGraph
            data={adjustedData}
            isLoading={isLoading}
            isEmpty={isEmpty}
            status={isSuccess ? InsightList.status.SUCCEEDED : InsightList.status.FAILED}
            index={index}
          />
        </Grid>
      ))}
    </Grid>
  )
}
